/**
 * Service for Places API
 * Manages places data (enterprises, clubs, favorites, etc.)
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/places';

/**
 * Place types
 */
export const PLACE_TYPES = {
    ENTERPRISE: 'enterprise',
    CLUB: 'club',
    FAVORITE: 'favorite',
    OTHER: 'other'
};

/**
 * Place type labels (French)
 */
export const PLACE_TYPE_LABELS = {
    enterprise: 'Entreprise',
    club: 'Club',
    favorite: 'Lieu favori',
    other: 'Autre'
};

/**
 * Map of lowercase keys to camelCase
 * API returns lowercase (placename, tvanum) but frontend expects camelCase (placeName, tvaNum)
 */
const keyMapping = {
    placename: 'placeName',
    placetype: 'placeType',
    googleaddress: 'googleAddress',
    tvanum: 'tvaNum',
    createdbyid: 'createdById',
    createdat: 'createdAt',
    updatedat: 'updatedAt',
    categid: 'categId',
    placeuuid: 'placeUuid',
    contactname: 'contactName',
    firmtype: 'firmType',
    banknum: 'bankNum',
    facebookurl: 'facebookUrl',
    twitterurl: 'twitterUrl',
    youtubeurl: 'youtubeUrl',
    googleurl: 'googleUrl',
    modifiedbyid: 'modifiedById'
};

/**
 * Normalize object keys to camelCase
 * Handles both lowercase (from API) and UPPERCASE keys
 */
const normalizeKeys = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                // Use mapping if exists, otherwise keep original key
                const newKey = keyMapping[lowerKey] || lowerKey;
                return [newKey, normalizeKeys(value)];
            })
        );
    }
    return obj;
};

/**
 * Get all places with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Places array
 */
export const getAllPlaces = async (options = {}) => {
    try {
        const { page = 1, limit = 20, search = '', placeType = '' } = options;

        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        if (search) {
            params.append('search', search);
        }

        if (placeType) {
            params.append('placeType', placeType);
        }

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching places:', error);
        throw error;
    }
};

/**
 * Get a place by ID
 * @param {number} id - Place ID
 * @returns {Promise<Object>} Place object
 */
export const getPlaceById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA);
    } catch (error) {
        console.error('Error fetching place:', error);
        throw error;
    }
};

/**
 * Create a new place
 * @param {Object} placeData - Place data
 * @returns {Promise<Object>} Created place
 */
export const createPlace = async (placeData) => {
    try {
        console.log('Creating place:', placeData);

        // Ensure placeType is set
        const dataToSend = {
            ...placeData,
            placeType: placeData.placeType || PLACE_TYPES.ENTERPRISE
        };

        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        const result = await response.json();

        if (!response.ok) {
            // Check for duplicate error
            if (result.message?.includes('duplicate') || result.message?.includes('unique')) {
                throw new Error('Ce lieu existe déjà (numéro de TVA ou adresse identique)');
            }
            throw new Error(result.message || 'Error creating place');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA)
        };
    } catch (error) {
        console.error('Error creating place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Update a place
 * @param {number} id - Place ID
 * @param {Object} placeData - Updated data
 * @returns {Promise<Object>} Updated place
 */
export const updatePlace = async (id, placeData) => {
    try {
        console.log('Updating place:', id, placeData);

        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(placeData)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Error updating place');
        }

        return {
            success: true,
            data: normalizeKeys(result.data || result.DATA)
        };
    } catch (error) {
        console.error('Error updating place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Delete a place
 * @param {number} id - Place ID
 * @returns {Promise<Object>} Result
 */
export const deletePlace = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error deleting place:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Get places for a specific user by type
 * @param {number} peopleId - User ID
 * @param {string} placeType - Place type filter (optional)
 * @returns {Promise<Array>} User's places
 */
export const getPlacesByPeopleId = async (peopleId, placeType = '') => {
    try {
        const params = new URLSearchParams({
            createdById: peopleId.toString()
        });

        if (placeType) {
            params.append('placeType', placeType);
        }

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        return normalizeKeys(result.data || result.DATA || []);
    } catch (error) {
        console.error('Error fetching user places:', error);
        return [];
    }
};

/**
 * Get user's enterprises
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's enterprises
 */
export const getUserEnterprises = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.ENTERPRISE);
};

/**
 * Get user's clubs
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's clubs
 */
export const getUserClubs = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.CLUB);
};

/**
 * Get user's favorite places
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} User's favorite places
 */
export const getUserFavorites = async (peopleId) => {
    return getPlacesByPeopleId(peopleId, PLACE_TYPES.FAVORITE);
};

/**
 * Link a place to a user (people_places junction)
 * @param {number} peopleId - User ID
 * @param {number} placeId - Place ID
 * @param {string} role - Role/relationship (owner, employee, worker, client, visitor)
 * @param {boolean} isFavorite - Add to favorites
 * @returns {Promise<Object>} Result
 */
export const linkPlaceToPeople = async (peopleId, placeId, role = 'visitor', isFavorite = false) => {
    try {
        const response = await fetch(`${API_BASE_URL}/link`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, placeId, role, isFavorite })
        });

        const result = await response.json();

        return {
            success: response.ok,
            data: normalizeKeys(result.data || result.DATA),
            message: result.message
        };
    } catch (error) {
        console.error('Error linking place to people:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Unlink a place from a user
 * @param {number} peopleId - User ID
 * @param {number} placeId - Place ID
 * @returns {Promise<Object>} Result
 */
export const unlinkPlaceFromPeople = async (peopleId, placeId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/unlink`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, placeId })
        });

        const result = await response.json();

        return {
            success: response.ok,
            message: result.message
        };
    } catch (error) {
        console.error('Error unlinking place from people:', error);
        return {
            success: false,
            message: error.message
        };
    }
};

/**
 * Get user's relation with a specific place
 * @param {number} placeId - Place ID
 * @returns {Promise<Object>} Relation data (role, isFavorite)
 */
export const getPlaceRelation = async (placeId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/relation/${placeId}?peopleId=${peopleId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();
        console.log('getPlaceRelation API response:', result);

        // Handle both lowercase and uppercase keys from ColdFusion
        const data = result.data || result.DATA;
        const success = result.success !== undefined ? result.success : result.SUCCESS;

        if (success && data && Object.keys(data).length > 0) {
            const normalized = normalizeKeys(data);
            console.log('getPlaceRelation normalized data:', normalized);
            return {
                success: true,
                data: normalized
            };
        }

        return {
            success: true,
            data: null // No relation exists
        };
    } catch (error) {
        console.error('Error fetching place relation:', error);
        return {
            success: false,
            data: null,
            message: error.message
        };
    }
};

export default {
    getAllPlaces,
    getPlaceById,
    createPlace,
    updatePlace,
    deletePlace,
    getPlacesByPeopleId,
    getUserEnterprises,
    getUserClubs,
    getUserFavorites,
    linkPlaceToPeople,
    unlinkPlaceFromPeople,
    getPlaceRelation,
    PLACE_TYPES,
    PLACE_TYPE_LABELS
};
